/**
 * Orbit ITSM - Branding Configuration
 * 
 * This configuration enables full white-labelling via environment variables.
 * Set your branding in .env or deployment environment.
 * 
 * Orbit ITSM is a product of RME Solutions Technology.
 */

export interface ClientBranding {
  // Company details (the organisation using the portal)
  companyName: string
  companyShortName: string
  
  // Support contact
  supportPhone?: string
  supportEmail?: string
  supportHours?: string
  
  // UI Customisation
  portalTitle: string
  portalSubtitle?: string
}

// ============================================================
// ENVIRONMENT-DRIVEN BRANDING (WHITE-LABEL)
// ============================================================

/**
 * All branding is driven from environment variables.
 * This allows each deployment to be fully white-labelled.
 * 
 * Required env vars:
 *   VITE_PORTAL_TITLE    - Main portal title (e.g., "ICG IT Service Desk")
 *   VITE_COMPANY_NAME    - Company name (e.g., "ICG Construction")
 *   VITE_COMPANY_SHORT_NAME - Short name for logo area (e.g., "ICG")
 * 
 * Optional env vars:
 *   VITE_SUPPORT_EMAIL   - Support email address
 *   VITE_SUPPORT_PHONE   - Support phone number
 *   VITE_SUPPORT_HOURS   - Support hours text
 */
export const branding: ClientBranding = {
  // Portal title - the main heading shown throughout the app
  portalTitle: import.meta.env.VITE_PORTAL_TITLE || 'IT Service Desk',
  
  // Company name - shown in subtitles and descriptions
  companyName: import.meta.env.VITE_COMPANY_NAME || 'Your Company',
  
  // Short name - used in logo placeholder
  companyShortName: import.meta.env.VITE_COMPANY_SHORT_NAME || 'IT',
  
  // Subtitle - defaults to company name if not set
  portalSubtitle: import.meta.env.VITE_PORTAL_SUBTITLE || import.meta.env.VITE_COMPANY_NAME || undefined,
  
  // Support contact info
  supportEmail: import.meta.env.VITE_SUPPORT_EMAIL,
  supportPhone: import.meta.env.VITE_SUPPORT_PHONE,
  supportHours: import.meta.env.VITE_SUPPORT_HOURS,
}

// ============================================================
// ORBIT ITSM PLATFORM INFO
// ============================================================

/**
 * Platform branding - shown subtly in footer.
 * This identifies the underlying platform without dominating the UI.
 */
export const orbitPlatform = {
  name: 'Orbit',
  fullName: 'Orbit ITSM',
  version: __APP_VERSION__,
  copyright: '© 2026 RME Solutions Technology',
  website: 'https://rmesolutions.com.au',
}

// ============================================================
// DOCUMENT TITLE HELPER
// ============================================================

/**
 * Sets the document title with portal branding
 * @param pageTitle - Optional page-specific title
 */
export function setDocumentTitle(pageTitle?: string): void {
  document.title = pageTitle 
    ? `${pageTitle} | ${branding.portalTitle}`
    : branding.portalTitle
}
